import { colors } from '@lmc/common/constants'
import type { CarePlan, CarePlanAdl, Form } from '@lmc/ts-common'
import { Popover, Text } from '@lmc/ui-kit'
import type { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { getNominative, isLowerThen564px } from '../../common/utils'
import { ImageSection } from '../../components/generic/ListItem/components'
import { history } from '../../store'
import { withRouter } from '../../withRouter'

type CarePlanExtended = {
  name?: string
  icon?: string
  color?: string
  carePlanAdl?: CarePlanAdl
} & CarePlan

/**
 * A linked risk assessment as the care plans list receives it. The ADLs
 * endpoint keeps only `form.name` / `form.iconText`, and leaves `form` out
 * entirely when the template no longer resolves, so nothing here may assume
 * the template is present.
 */
export type RiskAssessmentExtended = {
  id: string
  name?: string
  icon?: string
  form?: Pick<Form, 'name' | 'iconText'>
}

type Props = {
  riskAssessmentCarePlans: CarePlanExtended[]
  carePlanRiskAssessments: RiskAssessmentExtended[]
  /**
   * @deprecated
   *
   * not used anymore
   * we are using useParams now
   */
  router?: any
  carePlanNominative?: string
  allowRedirect?: boolean
  placement?: string
  hidePopover?: boolean
}

export function CarePlansPopup({
  riskAssessmentCarePlans,
  carePlanRiskAssessments,
  carePlanNominative,
  allowRedirect = true,
  placement,
  hidePopover
}: Props): ReactElement {
  const { id } = useParams()

  const showCarePlanRiskAssessmentsPopup = carePlanRiskAssessments?.length

  return (
    <Popover
      open={hidePopover ? false : undefined}
      placement={(placement as any) || 'bottomRight'}
      content={
        <div style={styles.popover}>
          {!showCarePlanRiskAssessmentsPopup &&
            riskAssessmentCarePlans?.map((item) => (
              <div
                style={styles.popoverItem}
                onClick={(e) => {
                  e.stopPropagation()
                  return (
                    allowRedirect &&
                    history.push(`/residents/${id}/care-plans/${item.id}`)
                  )
                }}
              >
                {/* @ts-ignore */}
                <ImageSection
                  popup
                  iconColor={item?.color || item?.carePlanAdl?.color}
                  icon={(item?.icon || item?.carePlanAdl?.icon) as string}
                />
                {item?.name || item?.carePlanAdl?.name}
              </div>
            ))}
          {showCarePlanRiskAssessmentsPopup &&
            carePlanRiskAssessments?.map((item) => (
              <div
                key={item.id}
                data-testid={`linked-risk-assessment-${item.id}`}
                style={styles.popoverItem}
                onClick={(e) => {
                  e.stopPropagation()
                  return (
                    allowRedirect &&
                    history.push(
                      `/residents/${id}/risk-assessments/${item?.id}`
                    )
                  )
                }}
              >
                {/*
       // @ts-ignore */}
                <ImageSection
                  popup
                  icon={item.icon}
                  iconText={item.form?.iconText}
                />
                {item.form?.name}
              </div>
            ))}
        </div>
      }
    >
      {!isLowerThen564px && (
        <div>
          {placement !== 'bottomLeft' && (
            <Text as="p2" style={styles.dot}>
              •
            </Text>
          )}
          {carePlanRiskAssessments?.length > 0 ? (
            <Text as="p2" style={styles.linkText}>
              {`${carePlanRiskAssessments?.length} ${
                carePlanRiskAssessments?.length === 1
                  ? 'Risk Assessment'
                  : 'Risk Assessments'
              }`}
            </Text>
          ) : (
            <Text as="p2" style={styles.linkText}>
              {`In ${riskAssessmentCarePlans?.length} ${getNominative(
                carePlanNominative,
                `${
                  riskAssessmentCarePlans?.length === 1
                    ? 'Care Plan'
                    : 'Care Plans'
                }`
              )}`}
            </Text>
          )}
        </div>
      )}
    </Popover>
  )
}

const styles = {
  popover: {
    cursor: 'pointer'
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 5,
    marginBottom: 5
  },
  dot: { marginLeft: 5, color: colors.darkgrey },
  linkText: {
    marginLeft: 5,
    color: colors.informationblue,
    cursor: 'pointer',
    textDecoration: 'underline'
  }
}

export default withRouter(CarePlansPopup)
